import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client.ts";
import type { DocumentResponse, DocumentVersionListResponse, DocumentVersionResponse, SaveDocumentResponse } from "../api/contract.gen.ts";
import { useServices } from "../app/services.ts";
import { label, shortTime, VOXIE_ID } from "../data/display.ts";
import { useStore } from "../data/store.ts";
import { Message } from "../chats/Message.tsx";
import { Icon } from "../ui/Icon.tsx";
import { MarkdownView, toggleCheckbox } from "./markdown.tsx";
import styles from "./DocumentView.module.css";

/** Saving 1.5 s after the last keystroke, like the app (DocumentScreens.SAVE_AFTER_MS). */
const SAVE_AFTER_MS = 1500;

/**
 * A shared document (docs/dokumente-konzept.md): read with clickable checklists, write in Markdown,
 * saved as the whole text with the version it started from; the server merges by line.
 */
export function DocumentView({ id, onBack }: { id: string; onBack: (conversationId?: string) => void }) {
  const services = useServices();
  const doc = useStore((s) => s.documents.get(id));
  const conversation = useStore((s) => (doc ? s.conversations.get(doc.conversation_id) : undefined));
  const messages = useStore((s) => (doc ? s.messagesOf(doc.conversation_id).filter((m) => m.response.document_id === id) : []));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [versions, setVersions] = useState<DocumentVersionResponse[] | null>(null);
  const [preview, setPreview] = useState<DocumentVersionResponse | null>(null);
  const [say, setSay] = useState("");
  const base = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    services.session.call<DocumentResponse>(`/v1/documents/${id}`).then(
      (d) => {
        services.store.documents.set(d.id, d);
        services.store.changed();
        void services.sync.loadMessages(d.conversation_id).catch(() => undefined);
      },
      () => setStatus("Das Dokument gibt es nicht mehr."),
    );
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving saves what is still pending.
  useEffect(() => () => void flush(), []); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(body: string, baseVersion: number) {
    setStatus("speichert …");
    try {
      const r = await services.session.call<SaveDocumentResponse>(`/v1/documents/${id}`, { method: "PUT", json: { base_version: baseVersion, body } });
      services.store.documents.set(id, r.document);
      services.store.changed();
      base.current = null;
      if (r.merged || r.conflict) setDraft(r.document.body);
      setStatus(r.conflict ? "Gleichzeitig geändert: deine Zeilen stehen direkt unter den anderen." : r.merged ? "mit den Änderungen der anderen zusammengeführt" : "gespeichert");
    } catch (e) {
      if (e instanceof ApiError && e.code === "VERSION_GONE") {
        // The base version was pruned: the draft goes below the current text, as the app does.
        const current = await services.session.call<DocumentResponse>(`/v1/documents/${id}`);
        await save(`${current.body}\n\n---\n\n${body}`, current.version);
      } else {
        setStatus("Nicht gespeichert – bitte noch einmal versuchen.");
      }
    }
  }

  async function flush() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const d = services.store.documents.get(id);
    const text = (document.getElementById("document-editor") as HTMLTextAreaElement | null)?.value;
    if (d && text !== undefined && text !== d.body && base.current !== null) await save(text, base.current);
  }

  function change(text: string) {
    if (!doc) return;
    setDraft(text);
    // A draft keeps the version it started from until the server took it.
    base.current ??= doc.version;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(text, base.current ?? doc.version), SAVE_AFTER_MS);
    setStatus("…");
  }

  async function patch(fields: { title?: string; archived?: boolean }) {
    const d = await services.session.call<DocumentResponse>(`/v1/documents/${id}`, { method: "PATCH", json: fields }).catch(() => null);
    if (d) {
      services.store.documents.set(id, d);
      services.store.changed();
    }
  }

  async function decide(what: "apply" | "dismiss") {
    const s = doc?.suggestion;
    if (!s) return;
    try {
      const r = await services.session.call<SaveDocumentResponse | DocumentResponse>(`/v1/documents/${id}/suggestions/${s.id}/${what}`, { method: "POST" });
      const d = "document" in r ? r.document : r;
      services.store.documents.set(id, d);
      services.store.changed();
      setDraft(null);
    } catch {
      setStatus("Dokument hat sich geändert");
    }
  }

  if (!doc) return <main className={styles.page}>{status || "Wird geladen …"}</main>;
  const body = editing ? (draft ?? doc.body) : doc.body;
  const name = (u: string) => (u === VOXIE_ID ? "Voxie" : u === services.store.me ? "dir" : label(conversation?.members.find((m) => m.user.id === u)?.user).split(" ")[0]);

  return (
    <main className={styles.page} aria-label={doc.title}>
      <header className={styles.head}>
        <button className={styles.icon} onClick={() => void flush().then(() => onBack(doc.conversation_id))} aria-label="Zurück">
          <Icon name="back" />
        </button>
        <div className={styles.title}>
          <b
            title="Klicken zum Umbenennen"
            onClick={() => {
              const t = prompt("Titel", doc.title)?.trim();
              if (t) void patch({ title: t.slice(0, 80) });
            }}
          >
            {doc.title}
          </b>
          <span>
            Version {doc.version} · geändert von {name(doc.updated_by)} · {shortTime(doc.updated_at)}
            {status && ` · ${status}`}
          </span>
        </div>
        <button className={styles.text} onClick={() => (editing ? void flush().then(() => (setEditing(false), setDraft(null))) : (setDraft(doc.body), setEditing(true)))}>
          {editing ? "Fertig" : "Bearbeiten"}
        </button>
        <button className={styles.text} onClick={() => services.session.call<DocumentVersionListResponse>(`/v1/documents/${id}/versions`).then((r) => setVersions(r.versions), () => setVersions([]))}>
          Versionen
        </button>
        <button className={styles.text} onClick={() => void navigator.clipboard.writeText(doc.body)}>
          Kopieren
        </button>
        <button className={styles.text} onClick={() => void patch({ archived: !doc.archived })}>
          {doc.archived ? "Zurückholen" : "Archivieren"}
        </button>
        {doc.created_by === services.store.me && (
          <button
            className={styles.danger}
            onClick={async () => {
              if (!confirm("Dokument löschen? Das geht nicht rückgängig.")) return;
              await services.session.call(`/v1/documents/${id}`, { method: "DELETE" }).catch(() => undefined);
              services.store.documents.delete(id);
              services.store.changed();
              onBack(doc.conversation_id);
            }}
          >
            Löschen
          </button>
        )}
      </header>

      <div className={styles.columns}>
        <article className={styles.doc}>
          {editing ? (
            <textarea id="document-editor" className={styles.editor} value={body} onChange={(e) => change(e.target.value)} onBlur={() => void flush()} maxLength={20000} autoFocus spellCheck />
          ) : (
            <div className={styles.markdown}>
              {doc.body.trim() ? (
                <MarkdownView
                  text={doc.body}
                  onToggle={(line) => {
                    const next = toggleCheckbox(doc.body, line);
                    services.store.documents.set(id, { ...doc, body: next });
                    services.store.changed();
                    void save(next, doc.version);
                  }}
                />
              ) : (
                <p className={styles.muted}>Noch leer. „Bearbeiten“ oder ins Dokument sprechen.</p>
              )}
            </div>
          )}
          {editing && <p className={styles.hint}># Überschrift · **fett** · *kursiv* · - Liste · - [ ] Aufgabe · &gt; Zitat · --- Linie</p>}
        </article>

        <aside className={styles.side}>
          {doc.suggestion && (
            <div className={styles.suggestion}>
              <b>
                <Icon name="sparkle" size={18} /> Voxie schlägt vor
              </b>
              <p>{doc.suggestion.explanation}</p>
              {doc.suggestion.added.map((l, i) => (
                <div key={`a${i}`} className={styles.added}>
                  + {l}
                </div>
              ))}
              {doc.suggestion.removed.map((l, i) => (
                <div key={`r${i}`} className={styles.removed}>
                  − {l}
                </div>
              ))}
              <div className={styles.row}>
                <button className={styles.primary} onClick={() => void decide("apply")}>
                  Übernehmen
                </button>
                <button className={styles.text} onClick={() => void decide("dismiss")}>
                  Verwerfen
                </button>
              </div>
            </div>
          )}
          <div className={styles.label}>ZUM DOKUMENT</div>
          <div className={styles.talk}>
            {messages.length === 0 && <p className={styles.muted}>Was hier geschrieben wird, gehört zu diesem Dokument. Voxie kann daraus Änderungen vorschlagen.</p>}
            {conversation && messages.map((m) => <Message key={m.response.id} message={m} conversation={conversation} onTopic={() => undefined} />)}
          </div>
          <form
            className={styles.say}
            onSubmit={(e) => {
              e.preventDefault();
              if (!say.trim()) return;
              void services.sendText(doc.conversation_id, say, { document_id: id }).then(() => setSay(""));
            }}
          >
            <input placeholder="Zum Dokument schreiben" value={say} onChange={(e) => setSay(e.target.value)} />
            <button aria-label="Senden">
              <Icon name="send" size={20} />
            </button>
          </form>
        </aside>
      </div>

      {versions && (
        <div className={styles.versions} role="dialog" aria-label="Versionen">
          <header>
            <b>Versionen</b>
            <button className={styles.icon} onClick={() => (setVersions(null), setPreview(null))} aria-label="Schließen">
              <Icon name="close" />
            </button>
          </header>
          <div className={styles.versionList}>
            {versions.map((v) => (
              <button
                key={v.version}
                className={preview?.version === v.version ? styles.versionOn : undefined}
                onClick={() => services.session.call<DocumentVersionResponse>(`/v1/documents/${id}/versions/${v.version}`).then(setPreview, () => undefined)}
              >
                Version {v.version} · {name(v.author_id)} · {shortTime(v.created_at)}
              </button>
            ))}
          </div>
          {preview && (
            <div className={styles.preview}>
              <MarkdownView text={preview.body ?? ""} />
              <button
                className={styles.primary}
                onClick={async () => {
                  const d = await services.session.call<DocumentResponse>(`/v1/documents/${id}/restore`, { method: "POST", json: { version: preview.version } }).catch(() => null);
                  if (d) {
                    services.store.documents.set(id, d);
                    services.store.changed();
                  }
                  setVersions(null);
                  setPreview(null);
                }}
              >
                Diese Version wiederherstellen
              </button>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
